import { useState } from "react";
import { format } from "date-fns";
import {
  ChevronDown,
  ChevronRight,
  Calendar,
  User,
  Building,
  Truck,
  MapPin,
  Newspaper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { AddClaimModal } from "./components/AddClaimModal";
import { getClaims } from "@/api/receiver/getClaims";
import { useQuery } from "@tanstack/react-query";
import { ExpanedClaim } from "./components/ExpandedClaim";

export default function ClaimsManagement() {
  const [expandedClaim, setExpandedClaim] = useState<string | null>(null);
  const [newDetail, setNewDetail] = useState({ message: "" });
  const [isAddClaimModalOpen, setIsAddClaimModalOpen] = useState(false);

  const {
    data: claims,
    isSuccess,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ["claims"],
    queryFn: () => getClaims({}),
  });

  const toggleExpanded = (claimId: string) => {
    setExpandedClaim(expandedClaim === claimId ? null : claimId);
    setNewDetail({ message: "" });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Claims Management</h1>
          <p className="text-muted-foreground">
            Manage linehaul load claims and track their progress
          </p>
        </div>
        <Button
          variant="default"
          className="ml-auto"
          onClick={() => {
            setIsAddClaimModalOpen(true);
          }}
        >
          Create Claim
        </Button>
        <AddClaimModal
          isOpen={isAddClaimModalOpen}
          setIsOpen={setIsAddClaimModalOpen}
        />
      </div>

      <div className="grid gap-6">
        {isSuccess &&
          claims.map((claim) => {
            const load = claim.load;
            const isExpanded = expandedClaim === claim.id;

            return (
              <Card key={claim.id} className="overflow-hidden">
                <CardHeader
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleExpanded(claim.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-xl">
                          Claim #{claim.id.slice(-6).toUpperCase()}
                        </CardTitle>
                        <Badge
                          className={
                            claim.state === "open"
                              ? "bg-green-500 text-white"
                              : "bg-gray-500 text-white"
                          }
                        >
                          {claim.state}
                        </Badge>
                        <div className="flex items-center gap-1 text-lg font-semibold ">
                          {formatCurrency(claim.claimAmount)}
                        </div>
                      </div>
                      {load && (
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Truck className="w-4 h-4" />
                            {load.name}
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {load.source} → {load.destination}
                          </div>
                          <div className="flex items-center gap-1">
                            <Building className="w-4 h-4" />
                            {claim.load.carrierName || "Unassigned"}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-sm min-w-[120px]">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <User className="w-3 h-3" />
                          {claim.createdBy}
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {format(claim.createdAt, "MMM dd, yyyy")}
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="pt-0">
                    <Separator className="mb-6" />
                    <ExpanedClaim
                      claim={claim}
                      newDetail={newDetail}
                      setNewDetail={setNewDetail}
                    />
                  </CardContent>
                )}
              </Card>
            );
          })}

        {isSuccess && claims.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Newspaper className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Claims Yet</h3>
            </CardContent>
          </Card>
        )}

        {isError && (
          <Card className="text-center py-12">
            <CardContent>
              <Newspaper className="w-6 h-6 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Claims Yet</h3>
            </CardContent>
          </Card>
        )}
        {isLoading &&
          Array.from(Array(3)).map((x, i) => {
            return (
              <Card
                key={i}
                className="text-center animate-pulse duration-1000 h-[106px]"
              >
                <CardHeader className=" hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-xl">
                          <div className="h-6 w-40 rounded bg-gray-200"></div>
                        </CardTitle>
                        <Badge className="bg-gray-400 text-white h-5 w-16"></Badge>
                        <div className="flex items-center gap-1 text-lg font-semibold ">
                          <div className="h-6 w-20 rounded bg-gray-200"></div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Truck className="w-4 h-4" />

                          <div className="h-2 w-32 rounded bg-gray-200"></div>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <div className="h-2 w-32 rounded bg-gray-200"></div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Building className="w-4 h-4" />
                          <div className="h-2 w-32 rounded bg-gray-200"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-sm min-w-[120px]">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <User className="w-3 h-3" />
                          <div className="h-2 w-20 rounded bg-gray-200"></div>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          <div className="h-2 w-20 rounded bg-gray-200"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            );
          })}
      </div>
    </div>
  );
}
